import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import COMPANY_INFO from '../../config/companyInfo';
import api from '../../services/api';

const CreditNotePrint = () => {
  const { chargeId } = useParams();
  const contentRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchPrintData(); }, [chargeId]);

  const fetchPrintData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/charges/${chargeId}/print-data`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: '#d32f2f' }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: '20px' }}>No data found</div>;

  const { charge, shipment, items, containers, partyInfo } = data;

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${(dt.getMonth()+1).toString().padStart(2,'0')}-${dt.getDate().toString().padStart(2,'0')}-${dt.getFullYear()}`;
  };
  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const debitItems = items.filter(i => (i.bill_type || 'Debit') === 'Debit');
  const creditItems = items.filter(i => i.bill_type === 'Credit');
  const totalDebit = debitItems.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  const totalCredit = creditItems.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
  const totalAmount = totalDebit - totalCredit;
  const paidAmount = Number(charge.paid_amount) || 0;
  const balanceDue = totalAmount - paidAmount;

  // Build container summary
  const containerSizes = {};
  containers.forEach(c => {
    const sz = c.tp_sz || 'N/A';
    containerSizes[sz] = (containerSizes[sz] || 0) + 1;
  });
  const cntrCount = Object.entries(containerSizes).map(([sz, cnt]) => `${sz} X ${cnt}`).join(', ');

  const totalPkgs = containers.reduce((sum, c) => sum + (Number(c.pkg_count) || 0), 0) || shipment?.package_count || '';
  const totalWeightKg = containers.reduce((sum, c) => sum + (Number(c.weight_kg) || 0), 0) || shipment?.weight_kg || '';
  const totalCBM = containers.reduce((sum, c) => sum + (Number(c.measurement_cbm) || 0), 0) || shipment?.measurement_cbm || '';

  const isPaid = charge.status === 'Paid';
  const agentName = charge.agent_name || '';
  const isDebitNote = totalDebit > totalCredit;
  const docTitle = isDebitNote ? 'DEBIT NOTE' : 'CREDIT NOTE';
  const docFilePrefix = isDebitNote ? 'DebitNote' : 'CreditNote';
  const isAir = charge.shipment_type === 'air_import' || charge.shipment_type === 'air-import';
  const masterLabel = isAir ? 'MAWB NO. :' : 'MASTER B/L NO. :';
  const masterNo = isAir ? (shipment?.mawb_no || shipment?.mbl_no || '') : (shipment?.mbl_no || '');
  const houseLabel = isAir ? 'HAWB NO. :' : 'HOUSE B/L NO. :';
  const houseNo = isAir ? (shipment?.hbl_no || '') : (shipment?.hbl_no || '');

  const getTPAddress = (info) => {
    if (!info) return '';
    return [info.address, info.city, info.state, info.zip, info.country].filter(Boolean).join(', ');
  };

  const labelStyle = { fontWeight: 'bold', fontSize: '10px', color: '#333', whiteSpace: 'nowrap', paddingRight: '8px', paddingBottom: '3px' };
  const valStyle = { fontSize: '10px', paddingBottom: '3px' };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <DocumentToolbar title={docTitle} contentRef={contentRef} pdfUrl={`/pdf/charge/${chargeId}/credit-note`} onClose={() => window.close()} fileName={`${docFilePrefix}-${charge.invoice_no || chargeId}.pdf`} />

      <div ref={contentRef} style={{ backgroundColor: 'white', padding: '35px 40px', maxWidth: '900px', margin: '20px auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.4' }}>
        {/* Company Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #c00' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {COMPANY_INFO.logo && <img src={COMPANY_INFO.logo} alt="" style={{ height: '52px', marginRight: '12px' }} />}
              <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>{COMPANY_INFO.name}</div>
              <div style={{ fontSize: '10px', color: '#555' }}>
              <div>{COMPANY_INFO.address}</div>
              <div>{COMPANY_INFO.cityStateZip}</div>
              <div>TEL: {COMPANY_INFO.tel}</div>
              <div>EMAIL: {COMPANY_INFO.email}</div>
            </div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>{docTitle}</div>
        </div>

        {/* Agent + D/C Details */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px', marginBottom: '20px' }}>
          {/* Agent */}
          <div style={{ flex: 1 }}>
            <div style={{ border: '1px solid #000', padding: '10px', minHeight: '80px' }}>
              <div style={{ fontWeight: 'bold', fontStyle: 'italic', fontSize: '10px', marginBottom: '4px' }}>AGENT</div>
              <div style={{ fontWeight: 'bold', fontSize: '11px' }}>{agentName || 'N/A'}</div>
              {partyInfo && (
                <div style={{ fontSize: '10px', marginTop: '2px' }}>
                  {partyInfo.address && <div>{partyInfo.address}</div>}
                  {(partyInfo.city || partyInfo.state || partyInfo.zip) && (
                    <div>{[partyInfo.city, partyInfo.state].filter(Boolean).join(', ')}{partyInfo.zip ? ` ${partyInfo.zip}` : ''}</div>
                  )}
                  {partyInfo.country && <div>{partyInfo.country}</div>}
                </div>
              )}
            </div>
          </div>

          {/* D/C Details Table */}
          <div style={{ minWidth: '300px' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
              D/C No. : {charge.invoice_no || ''}
            </div>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['D/C DATE', formatDate(charge.invoice_date)],
                  ['DUE DATE', formatDate(charge.due_date)],
                  ['TERMS', charge.due_date && charge.invoice_date ? (new Date(charge.due_date).getTime() === new Date(charge.invoice_date).getTime() ? 'COD' : `${Math.round((new Date(charge.due_date) - new Date(charge.invoice_date)) / (1000*60*60*24))} DAYS`) : ''],
                  ['TOTAL AMOUNT', formatCurrency(Math.abs(totalAmount))],
                ].map(([label, val], i) => (
                  <tr key={label} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '5px 8px', fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>{label}</td>
                    <td style={{ padding: '5px 8px' }}>{val}</td>
                    {i === 3 && <td style={{ padding: '5px 8px', fontWeight: 'bold' }}>CURRENCY</td>}
                    {i === 3 && <td style={{ padding: '5px 8px' }}>USD</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Shipment Info Section */}
        <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 30px' }}>
            <table style={{ fontSize: '10px' }}><tbody>
              <tr><td style={labelStyle}>OUR FILE NO. :</td><td style={valStyle}>{shipment?.file_no || ''}</td></tr>
              <tr><td style={labelStyle}>AGENT FILE NO. :</td><td style={valStyle}>{charge.agent_ref_no || ''}</td></tr>
              <tr><td style={labelStyle}>{masterLabel}</td><td style={valStyle}>{masterNo}</td></tr>
              <tr><td style={labelStyle}>{houseLabel}</td><td style={valStyle}>{houseNo}</td></tr>
              <tr><td style={labelStyle}>{isAir ? 'FLIGHT :' : 'VESSEL :'}</td><td style={valStyle}>{shipment?.vessel_flight_no || shipment?.flight_no || ''}</td></tr>
              <tr><td style={labelStyle}>COMMODITY :</td><td style={valStyle}></td></tr>
            </tbody></table>
            <table style={{ fontSize: '10px' }}><tbody>
              <tr><td style={labelStyle}>POR / ETD :</td><td style={valStyle}></td></tr>
              <tr><td style={labelStyle}>POL / ETD :</td><td style={valStyle}>{shipment?.port_of_loading ? `${shipment.port_of_loading}${shipment?.etd ? ' / ' + formatDate(shipment.etd) : ''}` : ''}</td></tr>
              <tr><td style={labelStyle}>DEL / ETA :</td><td style={valStyle}></td></tr>
              <tr><td style={labelStyle}>POD / ETA :</td><td style={valStyle}>{shipment?.port_of_discharge ? `${shipment.port_of_discharge}${shipment?.eta ? ' / ' + formatDate(shipment.eta) : ''}` : ''}</td></tr>
              <tr><td style={labelStyle}>F.DEST. / ETA :</td><td style={valStyle}>{shipment?.final_destination ? `${shipment.final_destination}${shipment?.final_eta ? ' / ' + formatDate(shipment.final_eta) : ''}` : ''}</td></tr>
              <tr><td style={labelStyle}>NO. OF PKGS :</td><td style={valStyle}>{totalPkgs ? `${Number(totalPkgs).toLocaleString()} CARTON(S)` : ''}</td></tr>
              <tr><td style={labelStyle}>KGS / LBS (G) :</td><td style={valStyle}>{totalWeightKg ? `${Number(totalWeightKg).toLocaleString(undefined, {minimumFractionDigits:2})} KGS` : ''}</td></tr>
              <tr><td style={labelStyle}>CBM / CFT :</td><td style={valStyle}>{totalCBM ? `${Number(totalCBM).toLocaleString(undefined, {minimumFractionDigits:2})} CBM` : ''}</td></tr>
              <tr><td style={labelStyle}>CNTR. COUNT :</td><td style={valStyle}>{cntrCount}</td></tr>
            </tbody></table>
          </div>
        </div>

        {/* Charges Table - with Debit/Credit columns */}
        <div style={{ marginBottom: '10px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderTop: '2px solid #000', borderBottom: '2px solid #000', backgroundColor: '#f9f9f9' }}>
                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', width: '35px' }}>M/H</th>
                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', width: '120px' }}>BL NO</th>
                <th style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>DESCRIPTION</th>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '50px' }}>UNIT</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', width: '70px' }}>RATE</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', width: '50px' }}>QTY</th>
                <th style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', width: '35px' }}>P/C</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', width: '80px' }}>DEBIT(+)</th>
                <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', width: '80px' }}>CREDIT(-)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const isDebit = (item.bill_type || 'Debit') === 'Debit';
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px 8px' }}>M</td>
                    <td style={{ padding: '6px 8px', fontWeight: 'bold', fontSize: '10px' }}>{masterNo}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>{item.freight_description || item.freight_code || ''}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.unit === 'Shipment' ? '-' : item.unit}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(item.rate)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(item.volume || 0).toFixed(2)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.pc || 'C'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{isDebit ? formatCurrency(item.amount) : ''}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{!isDebit ? formatCurrency(item.amount) : ''}</td>
                  </tr>
                );
              })}
              {/* Empty rows for spacing */}
              {items.length < 6 && Array.from({ length: 6 - items.length }).map((_, i) => (
                <tr key={`empty-${i}`}><td colSpan={9} style={{ padding: '6px 8px' }}>&nbsp;</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ borderTop: '2px solid #000', paddingTop: '8px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
            <div style={{ width: '300px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>TOTAL</span>
              <span>{formatCurrency(totalDebit)}</span>
              <span>{formatCurrency(totalCredit)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
            <div style={{ width: '200px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 'bold' }}>PAID AMOUNT</span>
              <span>{formatCurrency(paidAmount)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', borderTop: '1px solid #ddd', paddingTop: '6px' }}>
            <span style={{ fontWeight: 'bold', fontSize: '10px' }}>
              GRAND TOTAL BALANCE DUE TO {agentName}
            </span>
            <div>
              <span style={{ fontWeight: 'bold', marginRight: '10px' }}>USD</span>
              <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{formatCurrency(balanceDue)}</span>
            </div>
          </div>
          {isPaid && (
            <div style={{ textAlign: 'right', fontSize: '24px', fontWeight: 'bold', color: '#c00', marginTop: '4px' }}>PAID</div>
          )}
        </div>

        {/* Remark and Memo */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0', marginBottom: '15px', border: '1px solid #000' }}>
          <div style={{ padding: '10px', borderRight: '1px solid #000' }}>
            <div style={{ fontWeight: 'bold', textAlign: 'center', borderBottom: '1px solid #000', paddingBottom: '5px', marginBottom: '8px' }}>REMARK</div>
            <div style={{ fontSize: '9px', lineHeight: '1.5' }}>
              <p style={{ margin: '3px 0' }}>*TO INSURE PROPER CREDIT, PLEASE SHOW THE INVOICE NUMBER ON YOUR PAYMENT*</p>
              <p style={{ margin: '3px 0' }}>*PAY TO THE ORDER OF {COMPANY_INFO.name}*</p>
              <p style={{ margin: '3px 0' }}>{COMPANY_INFO.address}</p>
              <p style={{ margin: '3px 0' }}>{COMPANY_INFO.cityStateZip}</p>
            </div>
          </div>
          <div style={{ padding: '10px' }}>
            <div style={{ fontWeight: 'bold', textAlign: 'center', borderBottom: '1px solid #000', paddingBottom: '5px', marginBottom: '8px' }}>MEMO</div>
            <div style={{ fontSize: '9px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{charge.memo || ''}</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ fontSize: '10px', marginTop: '10px', borderTop: '1px solid #ddd', paddingTop: '10px', textAlign: 'center' }}>
          {COMPANY_INFO.name}
        </div>
      </div>
    </div>
  );
};

export default CreditNotePrint;
